import React, { memo, useCallback, useMemo, useState } from 'react';
import { View, ScrollView, TouchableOpacity } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  Space,
  Text,
  Divider,
  IconButton,
  IconBack,
  IconTelephone,
} from '@redlink/components/atoms';
import {
  PreviewShopPostCardTextBlock,
  PreviewLinksList,
  PreviewContactsList,
  PreviewBottomSheet,
} from '@redlink/components/muscles';
import { ImagesContainer } from './imagesContainer';
import { UserBadgeContainer } from './userBadgeContainer';
import { useNavigation } from '@react-navigation/native';
import { useValues } from '@redlink/assets/values';
import { useQuery, getShopContactsPreviewQuery } from '@redlink/services';
import { useLocale } from '@redlink/assets/locale';

export const PreviewProductDetail = memo(
  ({
    data: {
      page: { fullname, avatar },
      product,
    },
    pageName,
  }) => {
    const [contactsIsVisible, setContactsIsVisible] = useState(false);
    const { styles, colors } = useStyle();
    const { navigate } = useNavigation();
    const { routes } = useValues();
    const { dictionary } = useLocale();
    const {
      images,
      category: { title: categoryTitle },
      title,
      price,
      discount,
      description,
      links,
    } = product;

    const navigateToPage = useCallback(
      () => navigate(routes.preview.key, { pageName }),
      [navigate, routes, pageName]
    );

    const {
      data: { ShopContactsPreview: shopContacts },
    } = useQuery(getShopContactsPreviewQuery, {
      variables: { pageName },
    });

    const hasShopContacts = useMemo(
      () => shopContacts?.length > 0,
      [shopContacts]
    );

    return (
      <View style={styles.container}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <ImagesContainer data={images} />
          <View style={styles.bodyContainer}>
            <PreviewShopPostCardTextBlock
              {...{ title, categoryTitle, price, discount }}
            />
            <Space />
            <UserBadgeContainer
              avatarUrl={avatar?.thumbnailUrl}
              {...{ fullname }}
              onPress={navigateToPage}
            />
            {hasShopContacts && (
              <>
                <Space />
                <ShopContactButton onPress={() => setContactsIsVisible(true)} />
              </>
            )}
            {links.length > 0 && (
              <>
                <Space />
                <PreviewLinksList data={links} style={styles.linksContainer} />
              </>
            )}
            <Space />
            <Divider color={colors.saparator} style={styles.divider} />
            <Space />
            <Text style={styles.descriptionText}>{description}</Text>
          </View>
        </ScrollView>
        <IconButton style={styles.backIconContainer} onPress={navigateToPage}>
          <IconBack color={colors.gray23} />
        </IconButton>
        <PreviewBottomSheet
          title={dictionary.addShopContact}
          isVisible={contactsIsVisible}
          onDismiss={() => setContactsIsVisible(false)}
        >
          <PreviewContactsList
            data={shopContacts}
            style={styles.linksContainer}
          />
        </PreviewBottomSheet>
      </View>
    );
  }
);

const ShopContactButton = memo(({ onPress }) => {
  const { styles, colors } = useStyle();
  const { dictionary } = useLocale();
  return (
    <TouchableOpacity
      activeOpacity={0.7}
      style={styles.shopContactButton}
      onPress={onPress}
    >
      <IconTelephone color={colors.cardBackground} size={20} />
      <Space size={8} />
      <Text style={styles.shopContactButtonText}>
        {dictionary.addShopContact}
      </Text>
    </TouchableOpacity>
  );
});

const useStyle = createUseStyle(({ colors, size, isRtl }) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  bodyContainer: {
    padding: 24,
  },
  userContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  userAvatarImage: {
    width: 24,
    height: 24,
  },
  userTitleText: {
    color: colors.subtitle,
    fontSize: 14,
  },
  descriptionText: {
    color: colors.subtitle,
    fontSize: 14,
  },
  backIconContainer: {
    backgroundColor: colors.gray7,
    position: 'absolute',
    top: 16,
    ...(isRtl && { right: 16 }),
    ...(!isRtl && { left: 16 }),
  },
  linksContainer: {
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  shopContactButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingVertical: 16,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  shopContactButtonText: {
    color: colors.cardBackground,
  },
}));
